			/**
			 * @ngdoc service
			 * @name WebCoreModule.service:WcHttpRequestService
			 * @requires $rootScope
			 * @requires $q
			 * @requires $http
			 * @requires $window
			 * @requires $timeout
			 * @description
			 * The WcHttpRequestService wraps Angular's $http service. The wrapper provides caching, interceptors and default config params
			 * that apply app-wide to all requests. The service also provides wrappers for each of the http verbs, but these functions are
			 * better used when interacted with via the WcHttpEndpointPrototype, so they will not be documented here.
			 */

angular.module('WebCoreModule')
	.service('WcHttpRequestService', ['$rootScope', '$q', '$http', '$window', '$timeout', '$interpolate', 'WcAlertConsoleService',
		function($rootScope, $q, $http, $window, $timeout, $interpolate, WcAlertConsoleService) {

			this.configuration = {
				xsrfCookieName: 'WSL-credential',
				withCredentials: true,
				headers: {},
				timeout: 10000,
				baseUrl: '',
				cache: false,
				pingTrustPeriodMS: 1000,
				unAuthenticatedCallback: function() {
				},
				unprotectedPingRoute: 'unprotected/ping',
				queuedRequestMessages: {}
			};

			/**
			 * @ngdoc method
			 * @name WcHttpRequestService.configureDefaults
			 * @methodOf WebCoreModule.service:WcHttpRequestService
			 * @param {Object} config Options object to configure default values used by the WcHttpRequestService. Config properties that can be set in this object include:
			 *  - baseUrl: String value identifying the base url to build requests from (e.g., wwwdev.jab.ford.com/Jab7Web/api). Defaults to ''
			 *  - xsrfCookieName: String value used to identify a cookie that should always be sent. Defaults to 'WSL-credential'.
			 *  - withCredentials: Boolean value for whether or not credentials should be sent on requests. Defaults to true.
			 *  - headers: Object containing headers to send on each request. Defaults to {}.
			 *  - timeout: Integer to identify how long to wait, in MS, before timing out a connection attempt. Defaults to 10000ms.
			 *  - cache: Boolean to determine app-wide caching. Defaults to false, and typically should not be changed.
			 *  - pingTrustPeriodMS: Integer to identify a trust period when calling an endpoint to check connectivity. Defaults to 1000ms, and typically doesn't need to be changed.
			 *  - unAuthenticatedCallback: Function to be called when the service determines it has lost authentication.
			 *  - unprotectedPingRoute: String value identifying the route to use for the unprotected ping request
			 * @description
			 * The WcHttpRequestService configures itself with application-wide defaults when it starts up. The configureDefaults function allows
			 * you to override those defaults at the app level, so that the new parameters will apply to any request made via the service.
			 */
			this.configureDefaults = function(config) {
				angular.extend(this.configuration, config);
			};

			this.requestConfigurationMapper = function(config) {
				var httpConfig = angular.copy(this.configuration);
				delete httpConfig.baseUrl;
				delete httpConfig.pingTrustPeriodMS;
				delete httpConfig.unAuthenticatedCallback;
				delete httpConfig.unprotectedPingRoute;
				delete httpConfig.queuedRequestMessages;

				angular.extend(httpConfig, config);

				delete httpConfig.cache;

				return httpConfig;
			};

			this.urlBuilder = function(url) {
				// if we detect that the url contains a protocol from the endpoint, it's already a full url
				if(url.indexOf('http://') > -1 || url.indexOf('https://') > -1) {
					return url;
				} else {
					return this.configuration.baseUrl + url;
				}
			};

			this.get = function(url, config) {
				var interceptedParams = this.triggerRequestInterceptors(url, null, config);
				url = interceptedParams.url;
				config = interceptedParams.config;

				//if params are passed in, they go on the url only after hitting $http. we need them sooner for cache storage purposes
				var parseParams = function(params){
					var retString = '/';
					Object.keys(params).forEach(function(key){
						if(retString === '/') {
							retString = retString + key + ':' + params[key];
						}
						else {
							retString = retString + '-' + key + ':' + params[key];
						}
					});
					return retString;
				};
				var urlForCache = url;
				if(config && config.params) {
					urlForCache = url + parseParams(config.params);
				}

				// if cache is enabled, should we attempt to use cached data or refresh from the server?
				// set alwaysRefresh to never use cache unless offline
				// set forceRefresh to true to override default behavior and request from the server on demand
				if(config && config.cache && !config.alwaysRefresh && !config.forceRefresh) {
					var cachedData;
					if(config.cache === 'localStorage') {
						cachedData = {data: this.getFromLocalStorage(urlForCache)};
					}
					else if(config.cache === 'sessionStorage') {
						cachedData = this.getFromSessionStorage(urlForCache);
					}
					if(cachedData && cachedData.data) {
						return $q.when(cachedData);
					}
				}

				return $http.get(this.urlBuilder(url), this.requestConfigurationMapper(config)).then(angular.bind(this, function(response) {
					$rootScope.$broadcast('Online');
					response = this.triggerResponseInterceptors(response);

					if(config) {
						if(config.cache === 'localStorage') {
							this.storeInLocalStorage(urlForCache, response.data);
						}
						else if(config.cache === 'sessionStorage') {
							this.storeInSessionStorage(urlForCache, response.data);
						}
					}
					//SHANIKIA
					return {data: response.data, status: response.status};
				}), angular.bind(this, function(error) {

					this.triggerErrorInterceptors(error);

					//we got to the server, but it errored out. since we know the server is available, return the error object and let the app decide what to do.
					if(error.status > 0) {
						return $q.reject(error);
					}

					return this.getNetworkState().then(angular.bind(this, function(state) {
						if(state === 'offline' && config) {
							var cachedData;
							if(config.cache === 'localStorage') {
								cachedData = {data: this.getFromLocalStorage(urlForCache)};
							}
							else if(config.cache === 'sessionStorage') {
								cachedData = this.getFromSessionStorage(urlForCache);
							}

							if(!cachedData || !cachedData.data) {
								return $q.reject('Offline: No cached data available!');
							}
							else {
								return $q.when(cachedData).finally(function() {
									// we are not rejecting the promise but are still operating 'offline'
									$timeout(function() {
										$rootScope.$broadcast('Offline');
									});
								});
							}
						}
						else {
							return $q.reject(error);
						}
					}));
				}));
			};

			this.post = function(url, data, config) {
				var interceptedParams = this.triggerRequestInterceptors(url, data, config);
				url = interceptedParams.url;
				data = interceptedParams.data;
				config = interceptedParams.config;

				return $http.post(this.urlBuilder(url), data, this.requestConfigurationMapper(config)).then(angular.bind(this, function(response) {
					$rootScope.$broadcast('Online');
					response = this.triggerResponseInterceptors(response);
					return {data: response.data, status: response.status};
				}), angular.bind(this, function(error) {
					this.triggerErrorInterceptors(error);
					//only queue for unrecoverable errors
					if(error.status <= 0 && (config && config.offline === 'queue' && !config.fromQueue)) {
						this.queueRequest('post', url, data, config);
						//if we queue, return a success promise resolution
						error.status = 'queue';
						return $q.when(error);
					} else {
						//and if we don't, make it an error
						return $q.reject(error);
					}
				}));
			};

			this.put = function(url, data, config) {
				var interceptedParams = this.triggerRequestInterceptors(url, data, config);
				url = interceptedParams.url;
				data = interceptedParams.data;
				config = interceptedParams.config;

				return $http.put(this.urlBuilder(url), data, this.requestConfigurationMapper(config)).then(angular.bind(this, function(response) {
					$rootScope.$broadcast('Online');
					response = this.triggerResponseInterceptors(response);
					return {data: response.data, status: response.status};
				}), angular.bind(this, function(error) {
					this.triggerErrorInterceptors(error);
					//only queue for unrecoverable errors
					if(error.status <= 0 && (config && config.offline === 'queue' && !config.fromQueue)) {
						this.queueRequest('put', url, data, config);
						//if we queue, return a success promise resolution
						error.status = 'queue';
						return $q.when(error);
					} else {
						//and if we don't, make it an error
						return $q.reject(error);
					}
				}));
			};

			this.delete = function(url, config) {
				var interceptedParams = this.triggerRequestInterceptors(url, null, config);
				url = interceptedParams.url;
				config = interceptedParams.config;

				return $http.delete(this.urlBuilder(url), this.requestConfigurationMapper(config)).then(angular.bind(this, function(response) {
					$rootScope.$broadcast('Online');
					response = this.triggerResponseInterceptors(response);
					return {data: response.data, status: response.status};
				}), angular.bind(this, function(error) {
					this.triggerErrorInterceptors(error);
					//only queue for unrecoverable errors
					if(error.status <= 0 && (config && config.offline === 'queue' && !config.fromQueue)) {
						this.queueRequest('delete', url, undefined, config);
						//if we queue, return a success promise resolution
						error.status = 'queue';
						return $q.when(error);
					} else {
						//and if we don't, make it an error
						return $q.reject(error);
					}
				}));
			};

			this.getFromLocalStorage = function(key) {
				var cachedData = $window.localStorage.getItem(key);

				try {
					cachedData = angular.fromJson(cachedData);
				}
				finally {
					//noinspection ReturnInsideFinallyBlockJS
                    return cachedData;
				}
			};

			this.getFromSessionStorage = function(key) {
				var cachedData = $window.sessionStorage.getItem(key);

				try {
					cachedData = angular.fromJson(cachedData);
				}
				finally {
					//noinspection ReturnInsideFinallyBlockJS
                    return !cachedData ? cachedData : {data: cachedData};
				}
			};

			this.storeInLocalStorage = function(key, value) {
				if(!angular.isString(value)) {
					value = angular.toJson(value);
				}
				$window.localStorage.setItem(key, value);
			};

			this.storeInSessionStorage = function(key, value) {
				if(!angular.isString(value)) {
					value = angular.toJson(value);
				}
				$window.sessionStorage.setItem(key, value);
			};

			this.removeFromLocalStorage = function(key) {
				$window.localStorage.removeItem(key);
			};

			this.removeFromSessionStorage = function(key) {
				$window.sessionStorage.removeItem(key);
			};

			this.addMessageForQueuedRequest = function(verb, url, data, config, response){
				//set up default messages
				var message, type;
				if(response.status === 200) {
					message = 'Queued request submitted successfully: ' + verb + ' to endpoint ' + url + ', status: ' + response.status;
					type = 'success';
				}
				else if(response.status === 409) {
					message = 'Queued request failed to be submitted due to a conflict: ' + verb + ' to endpoint ' + url + ', status: ' + response.status;
					type = 'danger';
				}
				else if(response.status === 500) {
					message = 'Queued request failed to be submitted due to a server error: ' + verb + ' to endpoint ' + url + ', status: ' + response.status;
					type = 'danger';
				}

				//ensure a message object has been passed in
				if(this.configuration.queuedRequestMessages && Object.keys(this.configuration.queuedRequestMessages).length !== 0) {
					//if we can't match our url identically, we likely are dealing with a put or delete with an id on the url
					var messageExp;
					if(!this.configuration.queuedRequestMessages[verb][url]) {
						if(this.configuration.queuedRequestMessages[verb][url.substring(0, url.lastIndexOf('/'))][response.status]) {
							message = this.configuration.queuedRequestMessages[verb][url.substring(0, url.lastIndexOf('/'))][response.status];
							 messageExp = $interpolate(message);
							message = messageExp({verb: verb, url: url, data: data, config: config, response: response});
						}
					}
					else {
						if(this.configuration.queuedRequestMessages[verb][url][response.status]) {
							message = this.configuration.queuedRequestMessages[verb][url][response.status];
							 messageExp = $interpolate(message);
							message = messageExp({verb: verb, url: url, data: data, config: config, response: response});
						}
					}
				}

				//add the message
				WcAlertConsoleService.addMessage({
					message: message,
					type: type,
					multiple: false,
					errorOnStateChange: false,
					timeout: 20
				});
			};

			this.processOfflineQueue = function(deregisterListener) {
				//retrieve the current queue then clear it and the associated listener so it won't be triggered again.
				//remember that each attempted request could add itself back to the queue if it fails
				var currentQueue = this.getFromLocalStorage('offlineQueue');
				this.removeFromLocalStorage('offlineQueue');
				deregisterListener();

				var item = currentQueue.shift();
				while(item) {
					item.config.fromQueue = true;
					var requestPromise;
					if(item.verb === 'delete') {
						requestPromise = this.delete(item.url, item.config);
					}
					else {
						requestPromise = this[item.verb](item.url, item.data, item.config);
					}
					//handler for both the success and error cases
					var handler = angular.bind(this, function(item, response) {
						this.addMessageForQueuedRequest(item.verb, item.url, item.data, item.config, response);
					}, item);
					//chain to add messages, as the verb functions themselves don't add them
					requestPromise.then(handler, handler);
					item = currentQueue.shift();
				}
			};

			this.setupQueueTrigger = function(){
				var deregisterListener = $rootScope.$on('Online', angular.bind(this, function() {
					this.processOfflineQueue(deregisterListener);
				}));
			};

			this.queueRequest = function(verb, url, data, config){
				var currentQueue = this.getFromLocalStorage('offlineQueue');
				if(!currentQueue) {
					this.storeInLocalStorage('offlineQueue', [{verb: verb, url: url, data: data, config: config}]);
					this.setupQueueTrigger();
				}
				else {
					currentQueue.push({verb: verb, url: url, data: data, config: config});
					this.storeInLocalStorage('offlineQueue', currentQueue);
				}
			};

			//need to do this when the service is created to establish the queue processing trigger
			if(this.getFromLocalStorage('offlineQueue')) {
				this.setupQueueTrigger();
			}

			this.requestInterceptors = [];
			this.responseInterceptors = [];
			this.errorInterceptors = [];

			/**
			 * @ngdoc method
			 * @name WcHttpRequestService.addRequestIntercetor
			 * @methodOf WebCoreModule.service:WcHttpRequestService
			 * @param {Function} interceptor A function to trigger before requests are sent.
			 * @description
			 * The WcHttpRequestService allows you to include callback interceptor functions before a request is sent. These
			 * custom functions can adjust the request before it is sent to the server. Once attached, the service will always
			 * trigger all request interceptors before triggering the request itself.
			 */
			this.addRequestInterceptor = function(interceptor) {
				this.requestInterceptors.push(interceptor);
			};

			this.triggerRequestInterceptors = function(url, data, config) {
				var index = 0;
				while(index < this.requestInterceptors.length) {
					var newParms = this.requestInterceptors[index](url, data, config);
					if(newParms) {
						if(newParms.url) url = newParms.url;
						if(newParms.data) data = newParms.data;
						if(newParms.config) config = newParms.config;
					}
					index++;
				}
				return {url: url, data: data, config: config};
			};

			/**
			 * @ngdoc method
			 * @name WcHttpRequestService.addResponseIntercetor
			 * @methodOf WebCoreModule.service:WcHttpRequestService
			 * @param {Function} interceptor A function to trigger before responses are processed and returned.
			 * @description
			 * The WcHttpRequestService allows you to include callback interceptor functions after a service response is received
			 * and before the returned data is given to the application code. These custom functions can adjust the response data or
			 * trigger behaviors before the data is given to the application. Once attached, the service will always trigger all response
			 * interceptors before returning the data and the control flow back to the application.
			 */
			this.addResponseInterceptor = function(interceptor) {
				this.responseInterceptors.push(interceptor);
			};

			this.triggerResponseInterceptors = function(response) {
				var index = 0;
				while(index < this.responseInterceptors.length) {
					var newResponse = this.responseInterceptors[index](response);
					if(newResponse) {
						response = newResponse;
					}
					index++;
				}
				return response;
			};

			/**
			 * @ngdoc method
			 * @name WcHttpRequestService.addErrorIntercetor
			 * @methodOf WebCoreModule.service:WcHttpRequestService
			 * @param {Function} interceptor A function to trigger before error responses are processed and returned.
			 * @description
			 * The WcHttpRequestService allows you to include callback interceptor functions after an erroneous service response is received
			 * and before the error is given to the application code. These custom functions can adjust for the error or
			 * trigger behaviors before control is given back to the application. Once attached, the service will always trigger all error
			 * interceptors before returning the errors and the control flow back to the application.
			 */
			this.addErrorInterceptor = function(interceptor) {
				this.errorInterceptors.push(interceptor);
			};

			/**
			 These 'Interceptors' currently function more as a callback/event handler
			 Keeping name as interceptor to align with Restangular and other function names
			 There is potential for refactoring to support graceful error recovery
			 **/
			this.triggerErrorInterceptors = function(error) {
				var index = 0;
				while(index < this.errorInterceptors.length) {
					this.errorInterceptors[index](error);
					index++;
				}
			};

			/**
			 *
			 *    Network Detection
			 *
			 * **/
			this._lastPingRequestTimestamp = 0;
			this._trustedResponse = '';
			this._pendingRequests = 0;
			this._currentPingRequestPromise = {};
			this._networkStatusResponses = ['online', 'unauthenticated', 'offline'];


			/**
			 * @ngdoc method
			 * @name WcHttpRequestService.getNetworkState
			 * @methodOf WebCoreModule.service:WcHttpRequestService
			 * @description
			 * @returns {Promise} Promise wrapped value to identify the current state of the application's connectivity to its server.
			 * The WcHttpRequestService allows you to interrogate the status of the the application's connectivity to its application
			 * server. This status check is used behind the scenes across various webCore services to determine proper actions
			 * when connectivity is lost. This function can also be triggered by application code, if the need arises.
			 *
			 * For this function to work properly, the application's services need to expose an endpoint unprotected by authentication
			 * controls. This service will use that endpoint to determine whether the application is online, unauthenicated, or offline.
			 */
			/* can return statuses defined in _networkStatusResponses
			 *
			 * will always resolve promise, never reject - error case is a valid response
			 * */
			this.getNetworkState = function() {

				if(this._pendingRequests > 0) {
					// already pinging server, do not re-request and return saved promise
					return this._currentPingRequestPromise;
				} else {
					var currentTime = new Date();
					if(currentTime - this._lastPingRequestTimestamp > this.configuration.pingTrustPeriodMS) {
						this._pendingRequests++;
						this._lastPingRequestTimestamp = currentTime;
						this._trustedResponse = '';
					} else {
						// we are trusting that our last ping request was close enough
						return $q.when(this._trustedResponse);
					}
				}

				//needs to use $http itself here to ensure we don't cause issues with the network detection loop.
				this._currentPingRequestPromise = $http.get(this.urlBuilder(this.configuration.unprotectedPingRoute), this.requestConfigurationMapper(this.configuration)).then(angular.bind(this, function() {
					// we only query the network when there is an error in retrieving data from the server
					// if we can access the unprotected ping endpoint, we know the issue is most likely that the user is not logged in
					this._pendingRequests--;
					this._currentPingRequestPromise = {};
					this._trustedResponse = this._networkStatusResponses[1]; //unauthenticated
					// perform callback
					this.configuration.unAuthenticatedCallback();
					$rootScope.$broadcast('Unauthenticated');
					return $q.when(this._networkStatusResponses[1]);
				}), angular.bind(this, function() {
					// if we cannot access the unprotected ping endpoint, we must be offline
					this._pendingRequests--;
					this._currentPingRequestPromise = {};
					this._trustedResponse = this._networkStatusResponses[2]; // offline
					$rootScope.$broadcast('Offline');
					return $q.when(this._networkStatusResponses[2]);
				}));

				return this._currentPingRequestPromise;
			};
		}]);
